<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useForm, useField } from 'vee-validate'
import Loading from '../components/Loading.vue'
import {
  KkTextarea,
  KkTextField,
  KkValidationError,
  KkFormLabel
} from '../components/base'

const store = useStore()
const router = useRouter()
const { t } = useI18n()

const emit = defineEmits(['update-title', 'update-breadcrumbs', 'update-progress'])
const tabelogAttributes = computed(() => store.state.companiesStoreTabelogAttributes.data)
const tabelogAttributesLoading = computed(() => store.state.companiesStoreTabelogAttributes.loading)
const storeAttributes = computed(() => store.state.companiesLocationStore.data)
const storeLoading = computed(() =>store. state.companiesLocationStore.loading)
const loading = computed(() => storeLoading.value || tabelogAttributesLoading.value)
const readStoreTabelogAttributes = () => store.dispatch('companiesStoreTabelogAttributes/readStoreTabelogAttributes')
const updateStoreTabelogAttributes = (payload) => store.dispatch('companiesStoreTabelogAttributes/updateStoreTabelogAttributes', payload)
const readStore = ({ company_uuid, store_uuid }) => store.dispatch('companiesLocationStore/readStore', { company_uuid, store_uuid })
const { store_uuid, company_uuid } = useRoute().params

const schema = {
  prTitle: (value) => {
    if(value === '') {
      return true
    } else if (value.replace(/\n|\s/g, '').length < 5) {
      return t('pages.CompaniesLocationStoreTabelogEdit.customValidationMessage.prTitle.min')
    } else if(value.replace(/\n|\s/g, '').length > 45) {
      return t('pages.CompaniesLocationStoreTabelogEdit.customValidationMessage.prTitle.max')
    } else {
      return true
    }
  },
  prContext: (value) => {
    if(value === '') {
      return true
    } else if (value.replace(/\n|\s/g, '').length < 5) {
      return t('pages.CompaniesLocationStoreTabelogEdit.customValidationMessage.prContext.min')
    } else if(value.replace(/\n|\s/g, '').length > 200) {
      return t('pages.CompaniesLocationStoreTabelogEdit.customValidationMessage.prContext.max')
    } else {
      return true
    }
  },
}

const { handleSubmit, meta, setValues } = useForm({
  validationSchema: schema,
  initialValues: {
    prTitle: '',
    prContext: '',
  },
})

const { value: prTitle, errorMessage: prTitleError, handleChange: prTitleHandleChange } = useField('prTitle', undefined, { label: t('pages.CompaniesLocationStoreTabelogEdit.prTitle') })
const { value: prContext, errorMessage: prContextError, handleChange: prContextHandleChange } = useField('prContext', undefined, { label: t('pages.CompaniesLocationStoreTabelogEdit.prContext') })

const initData = () => {
  setValues({
    prTitle: tabelogAttributes.value.pr.title,
    prContext: tabelogAttributes.value.pr.context,
  })
}

const handleUpdateAttributes = handleSubmit(async (value) => {
  const payload = {
    tabelogs: {
      pr: {
        title: value.prTitle,
        context: value.prContext,
      }
    }
  }
  const isSuccess = await updateStoreTabelogAttributes(payload)
  if(isSuccess) router.push({ name: 'CompaniesLocationStore', params: { company_uuid: company_uuid, store_uuid: store_uuid } })
})

const load = async () => {
  await Promise.all([
    readStoreTabelogAttributes(),
    readStore({ company_uuid: company_uuid, store_uuid: store_uuid }),
  ])
  emit('update-title', t('pages.CompaniesLocationStoreTabelogEdit.title', { name: storeAttributes.value.attributes.name }))
  initData()
}

load()
</script>

<template>
  <div>
    <Loading v-if="loading" />
    <form v-else class="p-box" @submit.prevent="handleUpdateAttributes">
      <div class="p-box__body">
        <div class="container">
          <div>
            <KkFormLabel :text="`${ $t('pages.CompaniesLocationStoreTabelogEdit.prTitle') }(${ $t('labels.countCharacters', { count: prTitle.replace(/\n|\s/g, '').length, max: 45 }) })`" />
            <KkTextField :model-value="prTitle" :placeholder="$t('messages.noSettings')" @update:model-value="prTitleHandleChange" />
            <KkValidationError :text="prTitleError" />
          </div>
          <div class="prContext">
            <KkFormLabel :text="`${ $t('pages.CompaniesLocationStoreTabelogEdit.prContext') }(${ $t('labels.countCharacters', { count: prContext.replace(/\n|\s/g, '').length, max: 200 }) })`" />
            <KkTextarea v-model="prContext" @change="prContextHandleChange" />
            <KkValidationError :text="prContextError" />
          </div>
        </div>
      </div>
      <div class="p-box__footer">
        <div class="c-button c-button--right">
          <router-link
            :to="{ name: 'CompaniesLocationStore', params: { company_uuid: company_uuid, store_uuid: store_uuid } }"
            class="c-button__item c-button__item--outline-cancel"
          >
            {{ $t('labels.cancel') }}
          </router-link>
          <button
            class="c-button__item c-button__item--primary"
            :class="{'c-button__item--disabled': !meta.valid}" :disabled="!meta.valid"
          >
            {{ $t('labels.save') }}
          </button>
        </div>
      </div>
    </form>
  </div>
</template>

<style lang="scss" scoped>
.container {
  padding: 24px 24px 50px;
  border: 1px solid #E4E7EB;
  border-radius: 4px;
  .c-info, .c-formLabel {
    margin-top: 24px;
  }
  .c-textArea, .c-textField {
    width: 50%
  }
  .paymentMethod {
    padding: 24px;
    margin-top: 24px;
    border: 1px solid #E4E7EB;
    border-radius: 4px;
    width: 50%;
    &__container {
      padding: 24px;
      margin-top: 24px;
      border: 1px solid #E4E7EB;
      border-radius: 4px;
    }
  }

  .prContext {
    margin-top: 20px;
  }
}
.info {
  margin-top: 10px;
}
</style>
